import { useState, useEffect } from 'react';
import { LogOut, Shield, DollarSign, Users, UserCheck, AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { Loader } from './ui/Loader';
import { SubAdminSession } from './SubAdminLogin';
import { toast } from '../utils/toast';
import { API_BASE } from '../utils/supabase/info';
import { AdminEmployerVerification } from './AdminEmployerVerification';
import { AdminReports } from './AdminReports';
import { AdminEmailLists } from './AdminEmailLists';
import { AdminWithdrawals } from './AdminWithdrawals';
import { devError } from '../utils/logger';


interface SubAdminDashboardProps {
  session: SubAdminSession;
  accessToken: string;
  onLogout: () => void;
}

const MODULE_INFO = {
  revenue: { icon: DollarSign, label: 'Inntekt', color: 'purple' },
  users: { icon: Users, label: 'Bruker-oversikt', color: 'blue' },
  employers: { icon: UserCheck, label: 'Godkjenning av arbeidsgivere', color: 'green' },
  reports: { icon: AlertTriangle, label: 'Rapporter', color: 'red' },
  refunds: { icon: RefreshCw, label: 'Angreretter', color: 'orange' },
  emails: { icon: Mail, label: 'E-postlister', color: 'indigo' }
};

export function SubAdminDashboard({ session, accessToken, onLogout }: SubAdminDashboardProps) {
  const [activeModule, setActiveModule] = useState<string | null>(null);
  const [revenueStats, setRevenueStats] = useState<any>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifySession();
    fetchModuleData();
  }, []);

  const verifySession = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/admin/sub-admins/verify-session?admin_id=${session.adminId}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (!data.valid) {
        toast.error('Session utløpt - vennligst logg inn på nytt');
        onLogout();
      }
    } catch (error) {
      devError('Session verification error:', error);
    }
  };

  const fetchModuleData = async () => {
    try {
      // Fetch revenue stats if permission
      if (session.permissions.includes('revenue')) {
        const revenueRes = await fetch(
          `${API_BASE}/admin/revenue-stats`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'X-SubAdmin-Session': session.adminId
            }
          }
        );
        if (revenueRes.ok) {
          const data = await revenueRes.json();
          setRevenueStats(data);
        }
      }

      // Fetch user stats if permission
      if (session.permissions.includes('users')) {
        const statsRes = await fetch(
          `${API_BASE}/admin/stats`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'X-SubAdmin-Session': session.adminId
            }
          }
        );
        if (statsRes.ok) {
          const data = await statsRes.json();
          setUserStats(data);
        }
      }
    } catch (error) {
      devError('Error fetching module data:', error);
    } finally {
      setLoading(false);
    }
  };

  const logAction = async (action: string, module: string, targetId?: string, details?: any) => {
    try {
      await fetch(
        `${API_BASE}/admin/sub-admins/log-action`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            adminId: session.adminId,
            action,
            module,
            targetId,
            details
          })
        }
      );
    } catch (error) {
      devError('Error logging action:', error);
    }
  };

  const handleModuleClick = (module: string) => {
    setActiveModule(module);
    logAction('opened_module', module);
  };

  const handleLogout = () => {
    logAction('logged_out', 'admin');
    localStorage.removeItem('sub_admin_session');
    onLogout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader size="2xl" className="text-purple-600" />
      </div>
    );
  }

  // Show module view if selected
  if (activeModule) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveModule(null)}
              className="text-purple-600 hover:text-purple-800 transition-colors"
            >
              ← Tilbake
            </button>
            <h2 className="text-lg">{MODULE_INFO[activeModule as keyof typeof MODULE_INFO]?.label}</h2>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logg ut
          </button>
        </div>

        <div className="p-6">
          {activeModule === 'employers' && (
            <AdminEmployerVerification accessToken={accessToken} subAdminId={session.adminId} />
          )}
          {activeModule === 'reports' && (
            <AdminReports accessToken={accessToken} subAdminId={session.adminId} />
          )}
          {activeModule === 'emails' && (
            <AdminEmailLists accessToken={accessToken} subAdminId={session.adminId} />
          )}
          {activeModule === 'revenue' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl mb-4">Inntektsstatistikk</h3>
              {revenueStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Månedlig gjentakende inntekt</p>
                    <p className="text-2xl font-semibold text-purple-600">
                      {revenueStats.monthlyRecurring?.toLocaleString('nb-NO')} kr
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Aktive Plus-brukere</p>
                    <p className="text-2xl font-semibold text-blue-600">
                      {revenueStats.activePlusCount || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Arbeidsgiver Basis</p>
                    <p className="text-2xl font-semibold text-green-600">
                      {revenueStats.activeEmployerCount || 0}
                    </p>
                  </div>
                  <div className="bg-cyan-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Arbeidsgiver Pluss</p>
                    <p className="text-2xl font-semibold text-cyan-600">
                      {revenueStats.activeEmployerPlusCount || 0}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Ingen data tilgjengelig</p>
              )}
            </div>
          )}
          {activeModule === 'users' && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl mb-4">Brukerstatistikk</h3>
              {userStats ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Totalt antall brukere</p>
                    <p className="text-2xl font-semibold text-purple-600">
                      {userStats.totalUsers || 0}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Talenter</p>
                    <p className="text-2xl font-semibold text-blue-600">
                      {userStats.talentUsers || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Arbeidsgivere</p>
                    <p className="text-2xl font-semibold text-green-600">
                      {userStats.employerTypeUsers || 0}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Ingen data tilgjengelig</p>
              )}
            </div>
          )}
          {activeModule === 'refunds' && (
            <AdminWithdrawals accessToken={accessToken} subAdminId={session.adminId} />
          )}
        </div>
      </div>
    );
  }

  // Show dashboard overview
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl">
              Velkommen, {session.displayName || session.username}
            </h1>
            <p className="text-sm text-gray-600">
              Sist pålogget: {new Date().toLocaleDateString('nb-NO', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logg ut
          </button>
        </div>
      </div>

      {/* Modules Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-xl mb-6">Dine moduler</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {session.permissions.map((permission) => {
            const moduleInfo = MODULE_INFO[permission as keyof typeof MODULE_INFO];
            if (!moduleInfo) return null;

            const Icon = moduleInfo.icon;
            const colorClasses = {
              purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
              blue: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
              green: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
              red: 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
              orange: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
              indigo: 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700'
            };

            return (
              <button
                key={permission}
                onClick={() => handleModuleClick(permission)}
                className={`bg-gradient-to-br ${colorClasses[moduleInfo.color as keyof typeof colorClasses]} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all text-white group`}
              >
                <Icon className="w-12 h-12 mb-4" />
                <h3 className="text-lg font-semibold mb-2">{moduleInfo.label}</h3>
                <p className="text-sm opacity-90">Klikk for å åpne</p>
              </button>
            );
          })}
        </div>

        {session.permissions.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Ingen moduler tilgjengelige</p>
            <p className="text-sm text-gray-500 mt-2">
              Kontakt hovedadministrator for å få tilgang
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
